import React, { useEffect, useRef, useState } from 'react'
import { PencilIcon } from '@heroicons/react/24/outline'
import AutocompleteUser2 from './AutocompleteUser2'
import { Button } from '@material-tailwind/react'
import LrNoTable from './LrNoTable'
import AutocompleteLrNo from './AutocompleteLrNo'

const ManageDeliveryForm = ({ user, setUser, userList, setUserList, orderList, lrNumber, setLrNumber, handleLrNoData, handleScannedBarcode, lrNolist, removelrno, handleDeliveryBoy, setVehicileNo, vehicileNo }) => {
	const [isScannerOpen, setIsScannerOpen] = useState(false)
	const [scannerError, setScannerError] = useState('')
	const [isScannerSupported, setIsScannerSupported] = useState(false)
	const videoRef = useRef(null)
	const streamRef = useRef(null)
	const scanFrameRef = useRef(null)
	const lastScannedRef = useRef('')

	useEffect(() => {
		setIsScannerSupported(typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator?.mediaDevices?.getUserMedia)
	}, [])

	useEffect(() => {
		if (!isScannerOpen) {
			if (scanFrameRef.current) {
				cancelAnimationFrame(scanFrameRef.current)
				scanFrameRef.current = null
			}
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop())
				streamRef.current = null
			}
			if (videoRef.current) {
				videoRef.current.srcObject = null
			}
			return undefined
		}

		if (!isScannerSupported) {
			setScannerError('Camera barcode scanning is not supported in this browser.')
			setIsScannerOpen(false)
			return undefined
		}

		let isDisposed = false
		const detector = new window.BarcodeDetector({
			formats: ['code_128', 'code_39', 'codabar', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']
		})

		const scanVideo = async () => {
			if (isDisposed || !videoRef.current) {
				return
			}

			try {
				const barcodes = await detector.detect(videoRef.current)
				const rawValue = barcodes?.[0]?.rawValue
				if (rawValue && rawValue !== lastScannedRef.current) {
					lastScannedRef.current = rawValue
					await handleScannedBarcode(rawValue)
					setIsScannerOpen(false)
					return
				}
			} catch (error) {
				setScannerError('Unable to read barcode from camera.')
			}

			scanFrameRef.current = requestAnimationFrame(scanVideo)
		}

		const startScanner = async () => {
			try {
				setScannerError('')
				lastScannedRef.current = ''
				const stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: { ideal: 'environment' } },
					audio: false
				})
				if (isDisposed) {
					stream.getTracks().forEach((track) => track.stop())
					return
				}
				streamRef.current = stream
				if (videoRef.current) {
					videoRef.current.srcObject = stream
					await videoRef.current.play()
					scanFrameRef.current = requestAnimationFrame(scanVideo)
				}
			} catch (error) {
				setScannerError('Camera access was blocked or is unavailable.')
				setIsScannerOpen(false)
			}
		}

		startScanner()

		return () => {
			isDisposed = true
			if (scanFrameRef.current) {
				cancelAnimationFrame(scanFrameRef.current)
				scanFrameRef.current = null
			}
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop())
				streamRef.current = null
			}
		}
	}, [handleScannedBarcode, isScannerOpen, isScannerSupported])

	console.log(lrNumber)
	return (
		<>
			<div className='mx-4 my-6 md:mx-6'>
				<div className='rounded-[28px] border border-slate-200 bg-white px-6 py-6 shadow-[0_18px_42px_rgba(15,23,42,0.07)]'>
					<div className='mb-5 grid gap-3 sm:grid-cols-3'>
						<div className='rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3'>
							<p className='text-[10px] font-[GilroyBold] uppercase tracking-[0.16em] text-slate-400'>Assigned rider</p>
							<p className='mt-1 text-sm font-[GilroyBold] text-slate-900'>{user?.name || 'Not selected'}</p>
						</div>
						<div className='rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3'>
							<p className='text-[10px] font-[GilroyBold] uppercase tracking-[0.16em] text-slate-400'>Vehicle</p>
							<p className='mt-1 text-sm font-[GilroyBold] text-slate-900'>{vehicileNo || 'Pending'}</p>
						</div>
						<div className='rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3'>
							<p className='text-[10px] font-[GilroyBold] uppercase tracking-[0.16em] text-slate-400'>Orders queued</p>
							<p className='mt-1 text-sm font-[GilroyBold] text-slate-900'>{lrNolist?.length || 0}</p>
						</div>
					</div>
					<div className='mb-5 border-b border-slate-100 pb-4'>
						<h4 className='text-[1.15rem] font-[GilroyBold] text-slate-900'>Delivery assignment</h4>
						<p className='mt-1 text-sm font-[GilroyMedium] text-slate-500'>Choose a delivery partner, attach a vehicle number, and keep adding order numbers to the same trip.</p>
					</div>

					<div className='grid gap-5 xl:grid-cols-2'>
						<div className='rounded-[24px] border border-slate-200 bg-slate-50/70 p-5'>
							<p className='mb-3 text-[11px] font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Delivery partner</p>
							{Object.keys(user).length === 0 ? <AutocompleteUser2 selected={user} setSelected={setUser} placeholder="Search delivery boy or partner" data={userList} /> : <div className='rounded-[20px] border border-slate-200 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(15,23,42,0.04)]'>
								<div className='flex items-start justify-between gap-3'>
									<div>
										<h3 className='text-sm font-[GilroyBold] text-slate-900'>{user.name || '-'}</h3>
										<p className='mt-1 text-[13px] font-[GilroyMedium] text-slate-500'>{user.email || '-'}</p>
										<p className='mt-1 text-[13px] font-[GilroyMedium] text-slate-500'>{user.phone || user.contact || '-'}</p>
									</div>
									<button type='button' onClick={() => setUser({})} className='rounded-full p-2 text-slate-500 transition hover:bg-slate-100'>
										<PencilIcon className='h-5 w-5' />
									</button>
								</div>
							</div>}
						</div>

						<label className='rounded-[24px] border border-slate-200 bg-slate-50/70 p-5'>
							<span className='mb-3 block text-[11px] font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Vehicle number</span>
							<input value={vehicileNo} onChange={(e) => setVehicileNo(e.target.value)} placeholder='Enter vehicle number' className='h-14 w-full rounded-[18px] border border-slate-200 bg-white px-4 text-base font-[GilroyMedium] text-slate-900 shadow-[0_8px_20px_rgba(15,23,42,0.04)] outline-none transition placeholder:text-slate-300 focus:border-[#f97316] focus:ring-4 focus:ring-[#f97316]/10' />
						</label>
					</div>

					<div className='mt-5 rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,_#ffffff,_#f8fafc)] p-5'>
						<div className='mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between'>
							<div>
								<p className='text-[11px] font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Order bundle</p>
								<p className='mt-1 text-sm font-[GilroyMedium] text-slate-500'>Search an order number, or scan its barcode to add it to the current delivery assignment.</p>
							</div>
							<Button type='button' onClick={() => setIsScannerOpen((prev) => !prev)} className='rounded-full bg-slate-900 px-5 py-2 text-xs font-[GilroyBold] text-white shadow-none hover:bg-slate-800'>
								{isScannerOpen ? 'Close Scanner' : 'Scan Barcode'}
							</Button>
						</div>
						{isScannerOpen && (
							<div className='mb-4 rounded-[20px] border border-slate-200 bg-slate-950 p-4 text-white'>
								<p className='text-sm font-[GilroyBold]'>Camera scanner</p>
								<p className='mt-1 text-xs font-[GilroyMedium] text-slate-300'>Point the camera at the order barcode. The LR will be added automatically after detection.</p>
								<div className='mt-4 overflow-hidden rounded-[18px] border border-slate-700 bg-black'>
									<video ref={videoRef} muted playsInline className='h-64 w-full object-cover' />
								</div>
							</div>
						)}
						{scannerError ? <p className='mb-4 text-sm font-[GilroyMedium] text-red-500'>{scannerError}</p> : null}
						{!isScannerSupported ? <p className='mb-4 text-sm font-[GilroyMedium] text-slate-500'>Camera barcode scanning needs browser support for `BarcodeDetector`. If unavailable, use the search field or a hardware barcode scanner in the order input.</p> : null}
						<div className='max-w-[520px]'>
							{Object.keys(lrNumber).length === 0 ? <AutocompleteLrNo handleLrNoData={handleLrNoData} selected={lrNumber} setSelected={setLrNumber} placeholder="Search order number" data={orderList} /> : <div className='rounded-[20px] border border-slate-200 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(15,23,42,0.04)]'>
								<div className='flex items-start justify-between gap-3'>
									<div>
										<p className='text-[11px] font-[GilroyBold] uppercase tracking-[0.14em] text-slate-400'>Selected order</p>
										<h3 className='mt-1 text-sm font-[GilroyBold] text-slate-900'>{lrNumber}</h3>
									</div>
									<button type='button' onClick={() => setLrNumber({})} className='rounded-full p-2 text-slate-500 transition hover:bg-slate-100'>
										<PencilIcon className='h-5 w-5' />
									</button>
								</div>
							</div>}
						</div>

						<LrNoTable removelrno={removelrno} lrNolist={lrNolist} />
					</div>

					<div className='mt-6 flex justify-end'>
						<Button onClick={handleDeliveryBoy} className='rounded-full bg-[#f97316] px-8 py-3 text-sm font-[GilroyBold] text-white shadow-[0_14px_30px_rgba(249,115,22,0.2)] hover:bg-[#ea580c]'>Create Delivery Run</Button>
					</div>
				</div>
			</div>
		</>
	)
}

export default ManageDeliveryForm
